import React, { useState } from 'react';
import { Flex, Modal, Spin } from 'antd';
import { useDispatch, useIntl } from 'umi';
import dayjs from 'dayjs';
import { BranchModel, ServiceModel } from '@/typings/models/Location';
import { UserModel } from '@/typings/models/User';
import { AgentActionTag } from '@/components/ActionTag';
import LeadAssigneeDropdownFilters from '@/components/Filters/LeadAssigneeDropdownFilters';
import { updateServiceRequest } from '@/services/api/location';
import { fetchCalendarViewServiceRequests } from '@/services/api/serviceRequest';
import { processAPIResponseNow } from '@/utils/request';
import { F } from '@/utils/intl';
import { usePubSub } from '@/context/PubSubContext';
import { SERVICE_AGENTS_CHANGED } from '@/context/PubSubContext/events';

interface ServiceAgentsProps {
  service: ServiceModel;
  branch: BranchModel;
  onSuccess?: (data?: any) => void;
}

const ServiceAgents: React.FC<ServiceAgentsProps> = ({ service, branch, onSuccess }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { publish } = usePubSub();
  const [loading, setLoading] = useState(false);

  const handleUpdateAgents = async (agentIds: string[]) => {
    setLoading(true);
    const Response = await updateServiceRequest({
      params: { serviceId: service.id },
      payload: { agents: agentIds },
    });

    processAPIResponseNow(Response, {
      onSuccess: (data) => {
        onSuccess?.(data?.data || { agents: agentIds });
        // Only after the write landed — a refetch racing the PATCH would cache the
        // pre-update agent list back into the store.
        publish(SERVICE_AGENTS_CHANGED, {
          branchIds: [branch.id],
          serviceIds: [service.id],
        });
      },
      onEnd: () => {
        setLoading(false);
      },
    });
  };

  const handleAgentRemove = (agent: UserModel) => {
    setLoading(true);
    fetchCalendarViewServiceRequests({
      query: {
        assignee: agent.id,
        branch: branch.id,
        service_type: service.service_type?.uuid,
        status: 'confirmed,tentative',
        start_date: dayjs().format('YYYY-MM-DD'),
      },
    }).then((response) => {
      processAPIResponseNow(response, {
        onSuccess(data) {
          if (data.total === 0) {
            const newAgentIds = (service.agents || [])
              .filter((a) => a.id !== agent.id)
              .map((a) => a.id);
            handleUpdateAgents(newAgentIds);
          } else {
            Modal.confirm({
              title: intl.formatMessage({ id: 'pages.locations.cannotRemoveAgent' }, { defaultMessage: 'Cannot remove agent from service' }),
              content: intl.formatMessage({
                id: 'pages.locations.agentHasAppointments',
                defaultMessage: `This agent has ${data.total} upcoming appointment(s) for this service at this branch. Please reassign or cancel these appointments before removing the agent from the service.`
              }, { count: data.total }),
              okText: intl.formatMessage({ id: 'pages.locations.viewMeetings', defaultMessage: 'View Meetings' }),
              onOk() {
                window.open(`/crm/appointments?assignee=${agent.id}&branch=${branch.id}&service_type=${service.service_type?.uuid}&start_date=${dayjs().format('YYYY-MM-DD')}`, '_blank');
              },
            });
            setLoading(false);
          }
        },
        onError() {
          setLoading(false);
        }
      });
    });
  };

  const handleAgentAdd = (selectedId: string, selectedAssignee: UserModel) => {
    if (selectedId && selectedAssignee) {
      const currentAgentIds = (service.agents || []).map((a) => a.id);
      if (!currentAgentIds.includes(selectedId)) {
        handleUpdateAgents([...currentAgentIds, selectedId]);
      }
    }
  };

  return (
    <Spin spinning={loading}>
      <Flex vertical gap={16}>
        <LeadAssigneeDropdownFilters
          filterMode="select"
          showCreate
          hideDisabled
          onlyAssignable
          value={null}
          placeholder={intl.formatMessage({
            id: 'pages.locations.addAgentToService',
            defaultMessage: 'Add agent to service',
          })}
          excludeOptions={(service.agents || []).map((agent) => agent.id)}
          onChange={handleAgentAdd}
        />
        <Flex gap={8} align="center" wrap>
          {(service.agents || []).map((agent) => (
            <AgentActionTag
              key={agent.id}
              agent={agent}
              closable
              onClose={() => handleAgentRemove(agent)}
              size="small"
            />
          ))}
          {(!service.agents || service.agents.length === 0) && (
            <div style={{ color: '#999', fontSize: '12px', padding: '8px 0' }}>
              <F id="pages.locations.noAgentsAssigned" defaultMessage="No agents assigned to this service" />
            </div>
          )}
        </Flex>
      </Flex>
    </Spin>
  );
};

export default ServiceAgents;
